import PropTypes from 'prop-types';
import { Fragment } from 'react';
import cx from 'classnames';

import { Helmet } from 'core/libs/helmet';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';

import themePropTypes from 'core/utils/prop-types/theme';
import { capitalize } from 'core/utils/string-helper';
import { resolveScopedStyles } from 'core/utils/styled-jsx';

import H1 from 'core/components/H1';
import Button from 'core/components/Button';
import Link from 'core/components/Link';
import Popular from 'core/components/Popular';
import Bouesque from 'core/components/Bouesque';
import Feed from 'core/components/Feed';
import Drum from 'core/components/Drum';

import styles from './index.styl';

function reloadPage() {
  process.env.BROWSER_RUNTIME && window.location.reload();
}


// TODO https://jira.rambler-co.ru/browse/UCMS-8369

function ErrorPage(props, { host, amp }) {
  const {
    type,
    theme,
    isMobile,
    children,
    errorCode,
  } = props;
  const componentTheme = theme.pages[type];

  const {
    title,
    subtitle,
    imgUrl,
    imgWidth,
    buttonSize,
    buttonType,
    buttonText,
    background,
    card,
    padding,
    popular,
  } = componentTheme;

  const scoped = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .drumContainer
          padding ${popular.drumPadding}
      `}</style>
    </scope>
  );

  const popularParams = {
    renderer: isMobile ? Feed : Drum,
    containerClass: scoped.wrapClassNames('drumContainer'),
    interitemSpacing: popular.interitemSpacing,
    noCounter: true,
    arrowsShift: 20,
    itemWidth: '25%',
  };

  return (
    <Fragment>
      {type === 'notFound' && (
        <Helmet>
          <title>Страница не найдена</title>
          <meta name='description' content={`Страница 404, страница на сайте ${capitalize(host.replace(/(^www.)/g, ''))} не найдена`} />
        </Helmet>
      )}

      {type === 'error' && (
        <Helmet>
          <title>Ошибка</title>
          <meta name='description' content='Ошибка' />
        </Helmet>
      )}

      {children ? children : (
        <div className={cx('container', type === 'offline' && 'offline')}>
          <style jsx>{`
            .container
              background ${background}
              padding ${padding}

            .title
              font ${title.font}
              color ${title.color}

            .subtitle
              font ${subtitle.font}
              color ${subtitle.color}

            .popular
              background ${popular.background}
              padding ${popular.padding}

            .${styles.popularHeader}
              padding ${popular.header.padding}

            .${styles.popularTitle}
            .${styles.searchTitle}
              font ${popular.title.font}
              color ${popular.title.color}
          `}</style>
          {/* Стили в файле потому что он используеться и в amp версии */}
          <style jsx>{`
            .container
              &.offline
                padding 20px 20px 80px
                :global(.mobile) &
                  padding 20px 10px 40px

            .error
              display flex
              flex-direction column
              justify-content center
              align-items center

            .mainTitle
              text-align center

            .subtitle
              text-align center
              margin 20px 0 30px
              :global(.mobile) &
                margin 10px 0 20px

            .img
              max-width 100%
              margin-bottom 50px
              :global(.mobile) &
                margin-bottom 30px

            .popular
              margin-top 50px
              :global(.mobile) &
                margin-bottom 40px
          `}</style>
          <div className='error'>
            <H1 className='mainTitle'>
              <span className='title'>{title.name || errorCode}</span>
            </H1>
            <div className='subtitle'>{subtitle.name}</div>
            {imgUrl && (
              <img
                src={imgUrl}
                width={imgWidth}
                className='img'
                alt=''
              />
            )}
            {type === 'offline' ? (
              <Button
                size={buttonSize}
                type={buttonType}
                onClick={reloadPage}
              >
                Повторить
              </Button>
            ) : (
              <Link to='/'>
                <Button size={buttonSize} type={buttonType}>
                  {buttonText}
                </Button>
              </Link>
            )}
          </div>
          {card && !amp && (
            <div className='popular'>
              <Popular
                card={card}
                title='Популярные материалы'
                limit={8}
                sort='-views_count'
                include='all'
                headerComponent={({ children: popularTitle }) => {
                  if (popular.cardsOnly) {
                    return null;
                  }
                  return (
                    <div className={styles.popularHeader}>
                      <div className={styles.popularTitle}>
                        {popularTitle}
                      </div>
                      <div className={styles.searchWrapper}>
                        <div className={styles.searchTitle}>
                          Поиск по материалам
                        </div>
                        <Bouesque />
                      </div>
                    </div>
                  );
                }}
                dataQa='popular'
                {...popularParams}
              />
              <scoped.styles />
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
}

const pagesContract = `{
  title: {
    font,
    color,
  },
  subtitle: {
    font,
    color,
    name,
  },
  imgWidth,
  background,
  imgUrl,
}`;

ErrorPage.propTypes = {
  errorCode: PropTypes.number,
  type: PropTypes.oneOf(['notFound', 'error', 'sentryFallback', 'development', 'offline']),
  /** @ignore */
  theme: themePropTypes(`
    {
      pages: {
        error: ${pagesContract},
        notFound: ${pagesContract},
        sentryFallback: ${pagesContract},
        development: ${pagesContract},
      }
    }`
  ),
  /** @ignore */
  isMobile: PropTypes.bool,
};

ErrorPage.contextTypes = {
  host: PropTypes.string,
  amp: PropTypes.bool,
};

ErrorPage.defaultProps = {
  type: 'notFound',
};

const ErrorPageWithHOCs = withTheme(withBreakpoint(ErrorPage));
ErrorPageWithHOCs.displayName = 'ErrorPage';

export { ErrorPage as StorybookComponent };
export default ErrorPageWithHOCs;
